import { DOCUMENT } from "@angular/common";
import { Injectable, computed, inject, signal } from "@angular/core";
import { persistentAtom } from "@nanostores/persistent";
import { ANIMATIONS_ENABLED } from "lib/directives";

export enum Theme {
	Light = "light",
	Dark = "dark",
	System = "system",
}

const THEME_CHANGE_EVENT = "THEME_CHANGE";
const THEME_STORE = persistentAtom<Theme>("THEME", Theme.System);

const ANIMATIONS_ENABLED_STORE = persistentAtom<boolean>(
	"ANIMATIONS_ENABLED",
	true,
	{
		encode: (value) => value.toString(),
		decode: (value) => value === "true",
	},
);

@Injectable({
	providedIn: "root",
})
export class UserPreferenceService {
	private readonly _document = inject(DOCUMENT);
	private readonly _animationsEnabledToken = inject(ANIMATIONS_ENABLED);

	private readonly _animationsEnabled = signal<boolean>(
		ANIMATIONS_ENABLED_STORE.get(),
	);
	private readonly _theme = signal<Theme>(THEME_STORE.get());

	public readonly animationsEnabled = computed<boolean>(() =>
		this._animationsEnabled(),
	);
	public readonly theme = computed<Theme>(() => this._theme());

	constructor() {
		ANIMATIONS_ENABLED_STORE.subscribe((enabled) => {
			this._animationsEnabled.set(enabled);
			this._animationsEnabledToken.set(enabled);
		});
		THEME_STORE.subscribe((theme) => {
			this._theme.set(theme);
		});
	}

	public enableAnimations(): void {
		ANIMATIONS_ENABLED_STORE.set(true);
	}

	public disableAnimations(): void {
		ANIMATIONS_ENABLED_STORE.set(false);
	}

	public setTheme(theme: Theme): void {
		THEME_STORE.set(theme);
		const themeChangeEvent = new CustomEvent(THEME_CHANGE_EVENT, {
			detail: { theme },
		});
		this._document.dispatchEvent(themeChangeEvent);
	}
}
